import { types } from 'actions/organisation';
import {
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONPURCHASINGVIEW,
  ORGANISATIONWORKITEMVIEW,
} from 'components/Organisation/helpers';
import { defaultState } from './defaultState';

// Cross-cutting handlers that touch multiple tabs or global org-page state:
// state restore/migration, view switching, reset-all, notices, index checks
// and search cancellation.

export const sharedReducers = {
  [types.RESTORE_STATE]: (state, action) => {
    if (!action.payload) {
      return {
        ...defaultState,
        ...state,
      };
    }

    let restoredState = JSON.parse(JSON.stringify(action.payload));
    restoredState.organisationsCache = {};
    // SCIL-17744: People and Activity now share `filters`. Drop the legacy
    // `workItemFilters` split from older persisted sessions so it can't
    // resurrect the removed shape; the shared `filters` slice is authoritative.
    delete restoredState.workItemFilters;
    // Same migration for the per-org `category` group, renamed to
    // `workItemCategories` — a stale one would leak a wrong-case
    // `category:(...)` clause into buildQuery.
    Object.values(restoredState.filters ?? {}).forEach(orgFilters => {
      delete orgFilters.category;
    });
    restoredState = {
      ...defaultState,
      ...restoredState,
    };
    return restoredState;
  },

  [types.SET_VIEW]: (state, action) => {
    return {
      ...state,
      view: action.payload.view,
    };
  },

  [types.SET_VIEW_RESET_STATE]: (state, action) => ({
    ...state,
    view: action.payload.view,
    purchasingLoading: false,
  }),

  // Runs on org-page mount; the page's initial-load effects fire each tab's
  // first search, so this deliberately leaves the changed-filters flags alone.
  [types.RESET_ALL_FILTERS]: (state, action) => {
    const { organisationId } = action.payload;
    const shared = state.filters[organisationId];
    return {
      ...state,
      filters: {
        ...state.filters,
        [organisationId]: {
          workItemCategories: shared?.workItemCategories ?? {},
          date: shared && shared['fromRecentItems'] ? 'one' : 'all',
        },
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
      blockedForClinicalTrialExport: {
        ...state.blockedForClinicalTrialExport,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForPurchasingExport: false,
      purchasingLoading: false,
      purchasingFilters: {},
      purchasingSearchRequest: {
        ...state.purchasingSearchRequest,
        [organisationId]: null,
      },
    };
  },

  [types.DISMISS_NOTICE]: state => {
    return {
      ...state,
      showNotice: false,
    };
  },
  [types.DISMISS_CLINICAL_TRIAL_NOTICE]: state => {
    return {
      ...state,
      showClinicalTrialNotice: false,
    };
  },

  [types.CHECK_ORGANISATIONS_IN_INDEX_REQUEST]: (state, action) => ({
    ...state,
    organisationToCheckInIndex: action.payload.id,
  }),
  [types.CHECK_ORGANISATIONS_IN_INDEX_LOADING]: (state, action) => ({
    ...state,
    organisationsCache: {
      ...state.organisationsCache,
      ...action.payload.ids.reduce((acc, orgId) => {
        acc[orgId] = {
          loading: true,
        };
        return acc;
      }, {}),
    },
  }),
  [types.CHECK_ORGANISATIONS_IN_INDEX_SUCCESS]: (state, action) => ({
    ...state,
    organisationsCache: {
      ...state.organisationsCache,
      ...Object.keys(action.payload).reduce((acc, orgId) => {
        acc[orgId] = {
          exists: action.payload[orgId],
        };
        return acc;
      }, {}),
    },
    organisationToCheckInIndex: null,
  }),
  [types.CANCEL_SEARCH]: (state, action) => {
    state.cancelResearchersSearch?.();
    state.cancelWorkItemsSearch?.();
    state.cancelPurchasingSearch?.();
    state.cancelClinicalTrialsSearch?.();
    const { organisationId } = action.payload;

    if (state.view === ORGANISATIONPEOPLEVIEW) {
      return {
        ...state,
        organisationResearchers: {
          ...state.organisationResearchers,
          [organisationId]: {},
        },
        loading: { ...state.loading, [organisationId]: false },
        blockedForResearchersExport: {
          ...state.blockedForResearchersExport,
          [organisationId]: false,
        },
        totals: {
          ...state.totals,
          [organisationId]: {},
        },
        researcherSearchError: {
          ...state.researcherSearchError,
          [organisationId]: null,
        },
      };
    }

    if (state.view === ORGANISATIONWORKITEMVIEW) {
      return {
        ...state,
        workItemTotals: {
          ...state.workItemTotals,
          [organisationId]: {},
        },
        blockedForWorkItemExport: {
          ...state.blockedForWorkItemExport,
          [organisationId]: false,
        },
        workItems: {
          ...state.workItems,
          [organisationId]: {},
        },
        workItemLoading: { ...state.workItemLoading, [organisationId]: false },
        workItemSearchError: {
          ...state.workItemSearchError,
          [organisationId]: null,
        },
      };
    }

    if (state.view === ORGANISATIONPURCHASINGVIEW) {
      return {
        ...state,
        purchasingSearchRequest: {
          ...state.purchasingSearchRequest,
          [organisationId]: {},
        },
        purchasingLoading: false,
      };
    }

    if (state.view === ORGANISATIONCLINICALTRIALSVIEW) {
      return {
        ...state,
        clinicalTrialTotals: {
          ...state.clinicalTrialTotals,
          [organisationId]: {},
        },
        blockedForClinicalTrialExport: {
          ...state.blockedForClinicalTrialExport,
          [organisationId]: false,
        },
        organisationClinicalTrials: {
          ...state.organisationClinicalTrials,
          [organisationId]: {},
        },
        loadingClinicalTrialData: {
          ...state.loadingClinicalTrialData,
          [organisationId]: false,
        },
        clinicalTrialSearchError: {
          ...state.clinicalTrialSearchError,
          [organisationId]: null,
        },
        clinicalTrialChangedFilters: {
          ...state.clinicalTrialChangedFilters,
          [organisationId]: false,
        },
      };
    }

    return state;
  },
};
